/**
 * snapPager.ts — what a vertical swipe through a page-ended reader means.
 *
 * The reader opens a document one page at a time, each page owning the whole of the
 * surface it is read in, so "turning the page" is not a jump from one picture to
 * another: it is a *scroll* that has to end somewhere in particular. A finger drags
 * the paper, the neighbour comes up behind it, and when the hand lets go the reader
 * has to be settled on exactly one page — never left straddling two, which is how a
 * reader ends up reading the bottom of one page and the top of the next and calling
 * it a page.
 *
 * Two rules, and nothing else: where the drag may carry the reader, and which page a
 * released drag was asking for. Both are arithmetic about a pixel offset, so both
 * live here — the reader's own component decides what to *do* about the answer, and
 * `quizGate` decides whether the page it asks for may be reached at all.
 *
 * The threshold is a *fraction of a page*, not a distance or a speed. Speed is the
 * wrong question (the same flick made by a fast hand and a slow one is the same
 * flick, exactly as `pageTouch` argues for the sideways one), and a fixed distance is
 * wrong the moment the reader's window changes: sixty per cent of a page is sixty per
 * cent of a page in a phone and in a desktop window.
 */

/**
 * How much of the neighbouring page has to be on screen before it becomes the page
 * the reader gets.
 *
 * Six tenths: more than half, so the page the reader was on is the page they land on
 * back unless they clearly meant to leave it, and short enough that nobody has to
 * drag a page almost entirely out of sight to turn it.
 */
export const SNAP_FRACTION = 0.6

/**
 * How far a drag may carry the reader past the current page, in pages.
 *
 * One: a gesture moves the reader one page, so a drag can never skip a page — and a
 * page is where a document's questions live, which is the one thing a page turn must
 * not walk around (see `quizGate`). Where the drag ends is decided by the fraction
 * above; this is only how much paper the hand is allowed to take hold of.
 */
export const DRAG_LIMIT = 1

/**
 * How much of the reader's pull *past* the document's own ends the page gives, and how
 * far it may be pulled at all.
 *
 * The first page has no page above it and the last has none below, and a reader who
 * drags the wrong way at either end still needs the paper to say so: a page that does
 * not move at all reads as a page that has not noticed. The resistance is what every
 * touch surface does with the end of a list, and it is deliberately a *fraction* —
 * the paper moves, so the hand is answered, but it never moves far enough to look
 * like there was another page there.
 */
export const EDGE_GIVE = 0.32
export const EDGE_LIMIT = 0.5

/**
 * How much room a column needs at each end for that give to be *possible*.
 *
 * A scroll box cannot be dragged past its own content, so the give has to be space
 * the column actually has: the caller leaves this much above the first page and below
 * the last, and the reader who pulls at either end sees paper move into it. Exported
 * rather than left to the caller to multiply out, so the height reserved and the give
 * used cannot drift apart — a column with less room than this would be a paper that
 * stops dead at the ends, which is the one thing the give exists to avoid.
 */
export function edgeSlack(slot: number): number {
  // The loudest pull a drag can make is one page of travel, so this is what that pull
  // damps to: reserving less would be paper that stops dead before the gesture has
  // said everything it has to say.
  return slot * EDGE_GIVE
}

/** What a released vertical drag was asking for. */
export type SnapTarget = 'next' | 'previous' | 'current'

/**
 * Which page a released drag was asking for.
 *
 * `offset` is how far the pages had been carried when the hand let go, in pixels,
 * measured from the page the reader is on: positive means the *next* page has come up
 * from below (the reader is looking at the top of it), negative means the previous
 * page is still on screen above (the reader dragged the other way).
 *
 * A page dragged past the threshold in either direction is that page; anything less is
 * the page the reader was already on, and the caller puts it back — which is the
 * answer that matters most, because a reader who did not mean to turn the page is the
 * one the snapping is *for*.
 */
export function readSnap({
  offset,
  slot,
  fraction = SNAP_FRACTION,
}: {
  /** How far the drag carried the pages, in pixels, from the current page's own position. */
  offset: number
  /** One page's height: the same for every page, because every page owns the surface. */
  slot: number
  /** Overridable for the tests, and for nothing else. */
  fraction?: number
}): SnapTarget {
  if (slot <= 0) return 'current'
  const carried = Math.abs(offset) / slot
  if (carried < fraction) return 'current'
  return offset > 0 ? 'next' : 'previous'
}

/**
 * How far the pages are carried by a drag that has travelled `dy` pixels.
 *
 * `raw` is the movement the hand asked for, straight from the gesture: this is the
 * rule that says what the reader is allowed to *be* — one page's worth in either
 * direction, damped at the two ends of the document.
 */
export function dragOffset({
  raw,
  slot,
  page,
  totalPages,
}: {
  /** The finger's own travel, in pixels, positive towards the next page. */
  raw: number
  slot: number
  /** The page the reader is on, so the two ends of the document can be told apart. */
  page: number
  totalPages: number
}): number {
  if (slot <= 0) return 0
  const limit = slot * DRAG_LIMIT
  const bound = (want: number) =>
    Math.max(-limit, Math.min(limit, want * EDGE_GIVE))
  if (raw > 0 && page >= totalPages) return bound(Math.min(raw, limit))
  if (raw < 0 && page <= 1) return bound(Math.max(raw, -limit))
  return Math.max(-limit, Math.min(limit, raw))
}
